import React, { useState } from "react";
import { Box, Typography, IconButton, Collapse } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Emoji } from "emoji-picker-react";
import { ReactComponent as TargetIcon } from "../../assets/icons/crosshair.svg";
import Button from "../input/button";
import {
  setTaskTargeted as setTaskTargetedAction,
  removeTask,
} from "../../redux/actions/tasks";
import { setOverlayVisible } from "../../redux/actions/overlay";
import useTaskAPIClient from "../../api/task/hooks/useTaskAPIClient";
import useTaskDeadline from "../../hooks/tasks/useTaskDeadline";
import dayjs from "dayjs";

/**
 * Get a human-readable deadline label
 */
const getDeadlineLabel = (task) => {
  if (!task.deadline) return null;
  const now = dayjs();
  const deadlineTime = dayjs.unix(task.deadline);
  const hoursUntil = deadlineTime.diff(now, "hour");

  if (hoursUntil < 0) {
    const hoursAgo = Math.abs(hoursUntil);
    if (hoursAgo < 1) return "Just passed";
    if (hoursAgo < 24) return `${hoursAgo}h overdue`;
    return `${Math.round(hoursAgo / 24)} days overdue`;
  }
  if (hoursUntil < 1) return "Due in less than 1h";
  if (hoursUntil < 24) return `Due in ${hoursUntil}h`;
  return `Due in ${Math.round(hoursUntil / 24)} days`;
};

const TargetItem = ({
  task,
  onEdit,
  onUntarget,
  onComplete,
  loadingTargetId,
  completingTaskId,
}) => {
  const isUntargeting = loadingTargetId === task.taskId;
  const isCompleting = completingTaskId === task.taskId;
  const { closeToDeadline } = useTaskDeadline(
    task.deadline,
    task.created,
    () => {},
  );
  const deadlineLabel = getDeadlineLabel(task);

  const accentColor = closeToDeadline ? "#cb5538" : "#197b68";
  const hasDeadline = !!task.deadline;

  // Shared action buttons
  const actionButtons = (
    <Box
      className="targets-list__item-actions"
      sx={{
        display: "flex",
        gap: "8px",
        flexShrink: 0,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        text="Detarget"
        color="yellow"
        icon="detarget"
        collapsable={true}
        loading={isUntargeting}
        onClick={() => onUntarget(task.taskId)}
      />
      <Button
        text="Edit"
        color="orange"
        icon="edit"
        collapsable={true}
        onClick={() => onEdit(task.taskId)}
      />
      <Button
        text="Complete"
        color="green"
        icon="tick"
        collapsable={true}
        loading={isCompleting}
        onClick={() => onComplete(task.taskId)}
      />
    </Box>
  );

  if (hasDeadline) {
    // Two-section card: coloured title header + white body with deadline info & buttons
    return (
      <Box
        className="targets-list__item targets-list__item--has-deadline"
        sx={{
          borderRadius: "12px",
          marginBottom: "8px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
        }}
      >
        {/* Coloured title header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "10px 14px",
            background: accentColor,
            gap: "10px",
            borderRadius: "12px 12px 0 0",
          }}
        >
          {/* Icon circle — hidden below 440px */}
          <Box
            sx={{
              width: "32px",
              height: "32px",
              minWidth: "32px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "@media (max-width: 439px)": { display: "none" },
            }}
          >
            {task.emoji ? (
              <Emoji unified={task.emoji} size={20} />
            ) : (
              <TargetIcon
                style={{
                  width: "18px",
                  height: "18px",
                  fill: "transparent",
                  stroke: "#fff",
                  strokeWidth: "2px",
                }}
              />
            )}
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#fff",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {/* Inline emoji — shown below 440px only */}
            {task.emoji && (
              <Box
                component="span"
                sx={{
                  display: "none",
                  flexShrink: 0,
                  lineHeight: 1,
                  "@media (max-width: 439px)": { display: "inline-flex" },
                }}
              >
                <Emoji unified={task.emoji} size={16} />
              </Box>
            )}
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {task.name}
            </Box>
          </Typography>
        </Box>

        {/* White body: deadline info + action buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            background: "#fff",
            gap: "12px",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              ⏰ Deadline {" • "}{" "}
              {dayjs.unix(task.deadline).format("ddd, MMM D [at] h:mm A")}
            </Typography>
            {deadlineLabel && (
              <Typography
                variant="caption"
                sx={{
                  color: closeToDeadline ? accentColor : "rgba(0,0,0,0.5)",
                  fontWeight: closeToDeadline ? 700 : 400,
                  fontSize: "0.7rem",
                }}
              >
                {deadlineLabel}
              </Typography>
            )}
          </Box>
          {actionButtons}
        </Box>
      </Box>
    );
  }

  // Simple single-row card for tasks without deadline
  return (
    <Box
      className="targets-list__item"
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "10px 14px",
        background: accentColor,
        borderRadius: "12px",
        marginBottom: "8px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
        gap: "10px",
      }}
    >
      {/* Icon circle — hidden below 440px */}
      <Box
        sx={{
          width: "36px",
          height: "36px",
          minWidth: "36px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "@media (max-width: 439px)": { display: "none" },
        }}
      >
        {task.emoji ? (
          <Emoji unified={task.emoji} size={20} />
        ) : (
          <TargetIcon
            style={{
              width: "18px",
              height: "18px",
              fill: "transparent",
              stroke: "#fff",
              strokeWidth: "2px",
            }}
          />
        )}
      </Box>
      <Typography
        variant="body1"
        sx={{
          color: "#fff",
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {task.emoji && (
          <Box
            component="span"
            sx={{
              display: "none",
              flexShrink: 0,
              lineHeight: 1,
              "@media (max-width: 439px)": { display: "inline-flex" },
            }}
          >
            <Emoji unified={task.emoji} size={18} />
          </Box>
        )}
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {task.name}
        </Box>
      </Typography>
      {actionButtons}
    </Box>
  );
};

const TargetsList = ({ topTargets, allTargets }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loadingTargetId, setLoadingTargetId] = useState(null);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTaskTargeted, completeTask } = useTaskAPIClient();

  const hasMoreTargets = allTargets.length > 3;
  const displayTargets = expanded ? allTargets : topTargets;

  const handleEdit = (taskId) => {
    navigate("/edit-task", { state: { taskId } });
  };

  const handleUntarget = async (taskId) => {
    setLoadingTargetId(taskId);
    try {
      dispatch(setTaskTargetedAction({ taskId, targeted: false }));
      await setTaskTargeted(taskId, false);
    } finally {
      setLoadingTargetId(null);
    }
  };

  const handleComplete = async (taskId) => {
    const onConfirmCallback = async () => {
      setCompletingTaskId(taskId);
      try {
        dispatch(removeTask(taskId));
        await completeTask(taskId);
      } finally {
        setCompletingTaskId(null);
      }
      dispatch(setOverlayVisible({ visible: false }));
    };

    dispatch(
      setOverlayVisible({
        visible: true,
        overlayType: "confirm",
        confirmationText: "Have you completed this task?",
        confirmCallback: onConfirmCallback,
      }),
    );
  };

  const hasDeadlineWarning = allTargets.some((t) => {
    if (!t.deadline || !t.created) return false;
    const timeTillDeadline = t.deadline - dayjs().unix();
    if (timeTillDeadline < 0) return true;
    const createdInSeconds = Math.round(t.created / 1000);
    const timeToComplete = t.deadline - createdInSeconds;
    const timeToCompletePercent = Math.round(
      (timeTillDeadline / timeToComplete) * 100,
    );
    const withinMaxWarningWindow = timeTillDeadline <= 14 * 86400;
    return timeToCompletePercent <= 10 && withinMaxWarningWindow;
  });

  if (allTargets.length === 0) {
    return (
      <Box
        className="targets-list targets-list--empty"
        sx={{
          background: "rgba(255,255,255,0.95)",
          borderRadius: "20px",
          padding: "24px",
          marginBottom: "16px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
        >
          <Box
            sx={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
            }}
          >
            <TargetIcon
              style={{
                width: "24px",
                height: "24px",
                fill: "transparent",
                stroke: "#197b68",
                strokeWidth: "2px",
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
            Targets
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(0,0,0,0.6)",
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          No targeted tasks yet. Target a task to focus on what matters most!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="targets-list"
      sx={{
        background: "rgba(255,255,255,0.95)",
        borderRadius: "20px",
        padding: "24px",
        marginBottom: "16px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
      }}
    >
      <Box
        onClick={() => setCollapsed(!collapsed)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: collapsed ? 0 : "16px",
          cursor: "pointer",
          userSelect: "none",
          transition: "margin-bottom 0.3s ease",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
            }}
          >
            <TargetIcon
              style={{
                width: "24px",
                height: "24px",
                fill: "transparent",
                stroke: "#197b68",
                strokeWidth: "2px",
              }}
            />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Targets
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
              {allTargets.length} task{allTargets.length !== 1 ? "s" : ""} in
              focus
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {collapsed && hasDeadlineWarning && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "#cb5538",
                boxShadow: "0 2px 12px rgba(203, 85, 56, 0.4)",
                animation: "pulseWarning 1.2s ease-in-out infinite",
                "@keyframes pulseWarning": {
                  "0%": {
                    boxShadow:
                      "0 2px 12px rgba(203, 85, 56, 0.4), 0 0 0 0 rgba(203, 85, 56, 0.7)",
                    transform: "scale(1)",
                  },
                  "50%": {
                    transform: "scale(1.1)",
                  },
                  "100%": {
                    boxShadow:
                      "0 2px 12px rgba(203, 85, 56, 0.4), 0 0 0 8px rgba(203, 85, 56, 0)",
                    transform: "scale(1)",
                  },
                },
              }}
            >
              <WarningAmberIcon
                sx={{
                  color: "#fff",
                  fontSize: "20px",
                }}
              />
            </Box>
          )}
          <IconButton
            size="small"
            sx={{
              background: "rgba(25,123,104,0.1)",
              "&:hover": {
                background: "rgba(25,123,104,0.2)",
              },
            }}
          >
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={!collapsed} timeout={300}>
        <Box className="targets-list__items">
          {displayTargets.map((task) => (
            <TargetItem
              key={task.taskId}
              task={task}
              onEdit={handleEdit}
              onUntarget={handleUntarget}
              onComplete={handleComplete}
              loadingTargetId={loadingTargetId}
              completingTaskId={completingTaskId}
            />
          ))}
        </Box>

        {hasMoreTargets && !expanded && (
          <Typography
            variant="body2"
            onClick={() => setExpanded(true)}
            sx={{
              color: "#197B68",
              textAlign: "center",
              cursor: "pointer",
              marginTop: "8px",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Show {allTargets.length - 3} more target
            {allTargets.length - 3 !== 1 ? "s" : ""}
          </Typography>
        )}

        {hasMoreTargets && expanded && (
          <Typography
            variant="body2"
            onClick={() => setExpanded(false)}
            sx={{
              color: "#197B68",
              textAlign: "center",
              cursor: "pointer",
              marginTop: "8px",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Show less
          </Typography>
        )}
      </Collapse>
    </Box>
  );
};

export default TargetsList;
